import { useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';
import { IconPlus } from '@rossum/ui/icons/tabler';
import { IconButton, Stack, SvgIcon, Tooltip } from '@rossum/ui/material';
import { ACCEPTED_FILE_INPUT, MAX_ATTACHMENTS } from '../utils/attachments';

type AttachmentPlusButtonProps = {
  onPick: (files: FileList) => void;
  disabled?: boolean;
  canAddMore: boolean;
  dataCy?: string;
};

export const AttachmentPlusButton = ({
  onPick,
  disabled,
  canAddMore,
  dataCy,
}: AttachmentPlusButtonProps) => {
  const intl = useIntl();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target;
      if (files && files.length > 0) {
        onPick(files);
      }
      event.target.value = '';
    },
    [onPick]
  );

  return (
    <Stack>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_FILE_INPUT}
        multiple
        onChange={handleChange}
        style={{ display: 'none' }}
      />
      <Tooltip
        title={
          canAddMore
            ? intl.formatMessage({
                id: 'features.chat.attachments.tooltip',
              })
            : intl.formatMessage(
                { id: 'features.chat.attachments.maxReached' },
                { max: MAX_ATTACHMENTS }
              )
        }
      >
        <span>
          <IconButton
            data-cy={dataCy}
            onClick={handleClick}
            disabled={disabled || !canAddMore}
          >
            <SvgIcon fontSize="medium">
              <IconPlus />
            </SvgIcon>
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
};
